import { useEffect, useState } from "react";
import { GlobalTable } from "../../../global/components/GlobalTable";
import { useSearchParams } from "react-router-dom";
import useGet from "../../../global/hooks/useGet";
import { OrderColumns } from "./Columns";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import { OrderFilter } from "../Components/FilterComponent";
import { convertToUnix } from "../../../global/utils/unixToLocalTime";
import ConfirmModal from "../../../global/components/ConfirmModal";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import usePost from "../../../global/hooks/usePost";
import { OnSiteRegReportForm } from "../Components/OnSiteRegReportForm";

const initialFilterState = {
  OrderTime: "",
  OemNo: "",
  DealerNo: "",
  CustomerNo: "",
  Mac: "",
  OrderType: null,
  OrderItem: null,
  LicenseType: null,
  Free: null,
  OrderNo: "",
};

export const Order = () => {
  const [params, setParams] = useSearchParams({
    limit: 10,
    sort_by: "OrderNo",
    order: "desc",
  });
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const modParams = { ...Object.fromEntries([...params?.entries()]) } || {}; //
  const initialPagination = {
    current: parseInt(modParams.page) || 1,
    pageSize: parseInt(modParams.limit) || 10,
    total: 0,
  };
  const [pagination, setPagination] = useState(initialPagination);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [filter, setFilter] = useState(initialFilterState);
  const [resetTrigger, setResetTrigger] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [onSiteRegReportModal, setOnSiteRegReportModal] = useState(false);

  const childColumns = [
    {
      title: "License Item",
      dataIndex: "LicenseItem",
      key: "LicenseItem",
      width: 150,
    },
    {
      title: "Old License",
      dataIndex: "OldCode",
      key: "OldCode",
      width: 150,
    },
    {
      title: "New License",
      dataIndex: "NewCode",
      key: "NewCode",
      width: 150,
    },
    {
      title: "",
      dataIndex: "",
      key: "",
      width: 1,
    },
  ];

  const breadcrumbItems = [
    {
      label: "Order",
      href: "/order",
    },
  ];

  // to get table data from API server
  const query = new URLSearchParams(params);
  // Remove unwanted keys
  query.delete("pageSize");
  query.delete("total");
  query.delete("current");
  const updatedQuery = query.toString();

  const url = updatedQuery
    ? `${process.env.REACT_APP_API_URL}/api/orders/?${updatedQuery}`
    : `${process.env.REACT_APP_API_URL}/api/orders/`;
  const freeUrl = `${process.env.REACT_APP_API_URL}/api/orders/order-status/`;
  const { data, loading, error, refetch } = useGet(url);
  const {
    data: FreeData,
    loading: freeLoading,
    error: freeError,
    postData,
  } = usePost(freeUrl);

  // Add this helper same as Device.jsx:
  const getOrderTableExportQueryString = (searchParams) => {
    const q = new URLSearchParams(searchParams);
    q.delete("pageSize");
    q.delete("total");
    q.delete("current");
    q.delete("limit");
    return q.toString();
  };
  const orderListExportQs = getOrderTableExportQueryString(params);
  const onSiteRegReportUrl = orderListExportQs
    ? `${process.env.REACT_APP_API_URL}/api/orders/onsite-reg-report/?${orderListExportQs}`
    : `${process.env.REACT_APP_API_URL}/api/orders/onsite-reg-report/`;

  useEffect(() => {
    const matchedFilter = {};
    for (const key in initialFilterState) {
      if (modParams[key] !== undefined) {
        matchedFilter[key] = modParams[key];
      }
    }

    setFilter((prev) => ({
      ...prev,
      ...matchedFilter,
    }));
  }, []); //

  const paginationConfig = {
    current: pagination.current, //current page
    pageSize: pagination.pageSize, // items per page
    total: data?.count, // total items
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100],
    showTotal: (total, range) => `Total ${total} items ${range[0]}-${range[1]}`,
    onChange: (page, pageSize) => {
      setPagination({
        ...pagination,
        current: page,
        pageSize: pageSize,
      });

      const updatedParams = {
        ...Object.fromEntries([...params.entries()]),
        limit: pageSize,
        page: page,
      };
      setParams(updatedParams);
    },
  };

  const handleFilterChange = (e) => {
    const inputName = e.target.name;
    const inputValue = e.target.value.replace(/['""`''""]/g, "");
    setFilter({ ...filter, [inputName]: inputValue });
  };

  const handleFilter = (e) => {
    const filterDict = {};
    for (const key in filter) {
      if (
        filter[key] !== null &&
        filter[key] !== "" &&
        filter[key] !== undefined
      ) {
        if (key === "OrderTime") {
          filterDict[key] = convertToUnix(filter[key]);
        } else {
          filterDict[key] = filter[key];
        }
      }
    }
    const isEmpty = JSON.stringify(filterDict) === "{}";

    if (isEmpty) {
      handleReset();
    } else {
      const params = {
        limit: 10,
        sort_by: "OrderNo",
        order: "desc",
        page: 1,
        ...filterDict,
      };
      setPagination((prev) => ({ ...prev, current: 1 }));
      setParams(params);
      refetch();
    }
  };

  // reset
  const handleReset = (e) => {
    // Reset the filter state to the initial state
    setFilter({
      current: 1,
      pageSize: 10,
      total: 0,
    }); //
    setPagination({
      current: 1,
      pageSize: 10,
      total: 0,
    }); //
    // Define the new parameters for the API call
    const params = {
      limit: 10,
      sort_by: "OrderNo", // Make sure to specify sorting if necessary
      order: "desc",
    };
    setParams(params);
    refetch();
    setParams(params);
    setResetTrigger(!resetTrigger);
  };

  const onSelectAll = (selected) => {
    setSelectedRowKeys(selected);
    setShowModal(true);
  };
  const onSelectConfirm = async () => {
    try {
      if (selectedRowKeys.length) {
        if (data?.data?.every((item) => item.Free === "Yes")) {
          await postData({ ids: selectedRowKeys, Free: 0 });
        } else {
          await postData({ ids: selectedRowKeys, Free: 1 });
        }
      }
    } catch (error) {
      openErrorNotification("Something went wrong");
    }
    setShowModal(false);
    setSelectedRowKeys([]);
  };

  useEffect(() => {
    if (FreeData && !freeError) {
      openConfirmNotification("Success");
      refetch();
    } else if (freeError) {
      openErrorNotification(
        freeError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [FreeData, freeError]);

  return (
    <div>
      <div className="pb-4 -mt-4 pt-[6px]">
        <CommonBreadcrumb breadcrumbItems={breadcrumbItems} />
      </div>
      <div className="p-4 bg-white rounded-[4px] border border-gray-200">
        <OrderFilter
          filter={filter}
          handleFilterChange={handleFilterChange}
          handleFilter={handleFilter}
          handleReset={handleReset}
          setFilter={setFilter}
          params={params}
          onSiteRegReport={() => setOnSiteRegReportModal(true)} // ← ADD
        />
        {/* <ConfirmModal
          showModal={showModal}
          setShowModal={setShowModal}
          onDeleteConfirm={onDeleteConfirm}
          setSelectedRowKeys={setSelectedRowKeys}
        /> */}
        <GlobalTable
          columns={OrderColumns(
            setParams,
            params,
            data?.data,
            onSelectAll,
            refetch,
          )}
          dataSource={data?.data?.map((el) => ({ ...el, key: el.OrderNo }))}
          pagination={paginationConfig}
          loading={loading}
          resetTrigger={resetTrigger}
          expandable={{
            expandedRowKeys,
            rowExpandable: (record) =>
              Array.isArray(record?.OrderItems) && record.OrderItems.length > 0,
            expandedRowRender: (record) => (
              <div className="pl-8 pr-2 py-1">
                <GlobalTable
                  columns={childColumns}
                  dataSource={record.OrderItems.map((item) => ({
                    ...item,
                    key: item.ItemNo,
                  }))}
                  pagination={false}
                  tableProps={{ size: "small" }}
                />
              </div>
            ),
            onExpand: (expanded, record) => {
              if (expanded) {
                setExpandedRowKeys((prev) => [...prev, record.OrderNo]);
              } else {
                setExpandedRowKeys((prev) =>
                  prev.filter((k) => k !== record.OrderNo),
                );
              }
            },
            columnWidth: 40,
          }}
        />
      </div>
      <ConfirmModal
        showModal={showModal}
        setShowModal={setShowModal}
        onDeleteConfirm={onSelectConfirm}
        setSelectedRowKeys={setSelectedRowKeys}
        content={
          data?.data?.every((item) => item.Free === "Yes")
            ? "Do you want to Uncheck All?"
            : "Do you want to Check All?"
        }
      />
      {onSiteRegReportModal && (
        <OnSiteRegReportForm
          url={onSiteRegReportUrl}
          modalOpen={onSiteRegReportModal}
          setModalOpen={setOnSiteRegReportModal}
        />
      )}
    </div>
  );
};
